// =========================================
// BY ZY — State 2 Glass Style Resolver
// Maps 3 sliders (Blur/Refraction/Depth) to
// CSS variables that reproduce the reference
// glass generator screenshot.
//
// Reference baseline (blur=16, R=0.15, D=10):
//   background: rgba(255,255,255, 0.15)
//   backdrop-filter: blur(16px)
//   border: 1px solid rgba(255,255,255, 0.3)
//   box-shadow:
//     0 8px 32px rgba(0,0,0, 0.1),
//     inset 0 1px 0 rgba(255,255,255, 0.5),
//     inset 0 -1px 0 rgba(255,255,255, 0.1),
//     inset 0 0 20px 10px rgba(255,255,255, 0.12)
// =========================================

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Compute CSS variable map for a State 2 glass card.
 *
 * @param {object|undefined} raw — { blur, refraction, depth } from overrides
 * @returns {object} — CSS custom properties for React inline style, or {}
 */
export function getState2GlassVars(raw) {
    if (!raw) return {};

    const hasAny = (
        typeof raw.blur === 'number' ||
        typeof raw.refraction === 'number' ||
        typeof raw.depth === 'number'
    );
    if (!hasAny) return {};

    // Defaults match reference screenshot: blur=16, refraction=0.15, depth=10
    const B = typeof raw.blur === 'number' ? raw.blur : 16;
    const R = typeof raw.refraction === 'number' ? raw.refraction : 0.15;
    const D = typeof raw.depth === 'number' ? raw.depth : 10;

    // --- BLUR ---
    const vars = { '--g-blur': `${B}px` };

    // --- BACKGROUND ALPHA ---
    // Reference: 0.15 at R=0.15. Range: 0.08..0.30
    // Formula: maps R linearly
    vars['--g-bg-alpha'] = clamp(R * 1.0, 0.05, 0.30).toFixed(3);

    // --- BORDER ALPHA ---
    // Reference: 0.3 at R=0.15. Range: 0.15..0.45
    // Higher refraction = more visible border
    vars['--g-border'] = clamp(0.15 + R * 1.0, 0.15, 0.45).toFixed(3);

    // --- OUTER SHADOW ---
    // Reference: 0.1 at D=10. Range: 0.05..0.25
    vars['--g-shadow'] = clamp(0.05 + D * 0.005, 0.05, 0.25).toFixed(3);

    // --- INSET TOP HIGHLIGHT ---
    // Reference: 0.5 at R=0.15. Range: 0.20..0.70
    // Refraction drives specular intensity
    vars['--g-inset-top'] = clamp(0.20 + R * 2.0, 0.20, 0.70).toFixed(3);

    // --- INSET BOTTOM ---
    // Reference: 0.1 at R=0.15. Range: 0.05..0.20
    vars['--g-inset-bottom'] = clamp(0.05 + R * 0.35, 0.05, 0.20).toFixed(3);

    // --- INNER GLOW (the big inset glow that defines the generator look) ---
    // Reference: inset 0 0 20px 10px at D=10
    // Depth controls the size and strength
    vars['--g-glow-blur'] = `${clamp(10 + D * 1.0, 5, 40).toFixed(0)}px`;
    vars['--g-glow-spread'] = `${clamp(4 + D * 0.6, 2, 20).toFixed(0)}px`;
    // Glow alpha: MUST stay subtle to avoid white slab
    // Reference has alpha=1 on rgba(255,255,255,...) but in practice
    // it looks like ~0.10-0.15 because it's a soft inset
    vars['--g-glow-alpha'] = clamp(0.06 + R * 0.4 + D * 0.003, 0.04, 0.20).toFixed(3);

    // --- SPECULAR OVERLAY ---
    // Overlay inner ring
    vars['--g-ov-ring'] = clamp(0.10 + R * 0.5, 0.08, 0.30).toFixed(3);
    // Overlay top
    vars['--g-ov-top'] = clamp(0.06 + R * 0.3, 0.04, 0.20).toFixed(3);
    // Overlay bottom
    vars['--g-ov-bottom'] = clamp(0.08 + R * 0.4, 0.06, 0.25).toFixed(3);

    // --- TOP/LEFT SHEEN LINES ---
    // Refraction drives their brightness
    vars['--g-line-top'] = clamp(0.20 + R * 1.5, 0.15, 0.60).toFixed(3);
    vars['--g-line-left'] = clamp(0.15 + R * 1.0, 0.10, 0.45).toFixed(3);

    return vars;
}
